import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import classNames from 'classnames';
import debounce from 'lodash.debounce';
import { Select as AntdSelect } from 'antd';
import {
  RefSelectProps, SelectProps as AntdSelectProps,
} from 'antd/lib/select';
import { ArrowExpandDownIcon } from "components/Icons";
import { DeleteButton } from './DeleteButton';
import { SelectPropsCommon, SelectOptionType } from './types';

import cn from './styles.module.scss';

const { Option, OptGroup } = AntdSelect;

export interface SingleSelectProps<
  ValueType = any,
  OptionType extends SelectOptionType = SelectOptionType,
> extends SelectPropsCommon, Omit<AntdSelectProps<ValueType, OptionType>, 'onChange'> {
  multiple?: false,
  onChange?: (value: ValueType, option?: OptionType) => void,
  labelFormatter?: (label: React.ReactNode, selected: boolean) => string,
  onDelete?: (value: ValueType) => void,
}

export function SingleSelect<
  ValueType = any,
  OptionType extends SelectOptionType = SelectOptionType,
>(props: SingleSelectProps<ValueType, OptionType>) {
  const {
    options = [],
    value,
    placeholder,
    labelFormatter,
    onChange,
    onDelete,
    debounceTime,
    popupClassName,
    ...restProps
  } = props;

  const [selectValue, setSelectValue] = useState(value);
  const ref = useRef<RefSelectProps>(null);

  useEffect(() => {
    setSelectValue(value);
  }, [value]);

  const handleSelectChange = useCallback(
    debounceTime
      ? debounce((newValue, option) => onChange && onChange(newValue, option), debounceTime)
      : onChange as any,
    [onChange],
  );

  const handleChange = (newValue: any, option: any) => {
    setSelectValue(newValue);
    handleSelectChange(newValue, option);
  };

  return (
    <AntdSelect
      ref={ref}
      value={selectValue}
      popupMatchSelectWidth={false}
      menuItemSelectedIcon={restProps.menuItemSelectedIcon || null}
      maxTagCount={0}
      placeholder={placeholder}
      showSearch={{
        optionFilterProp: 'label'
      }}
      onChange={handleChange}
      suffixIcon={<ArrowExpandDownIcon style={{ fontSize: 22, marginRight: -5 }} />}
      size="large"
      {...restProps}
      optionLabelProp={onDelete ? 'label' : (restProps.optionLabelProp || 'children')}
      classNames={{
        ...restProps.classNames,
        popup: {
          ...restProps.classNames?.popup,
          root: classNames(
            cn.singleDropdown,
            typeof restProps.classNames !== 'function' && restProps.classNames?.popup?.root,
            popupClassName
          )
        }
      }}
    >
      {options.map((option) => {
        if (option.options) {
          return (
            <OptGroup label={option.label} key={option.label as string}>
              {option.options.map((subOption) => (
                <Option
                  key={subOption.value}
                  label={subOption.label}
                  value={subOption.value}
                >
                  {subOption.label}
                  {onDelete && subOption.value !== undefined && (
                    <DeleteButton onClick={() => onDelete(subOption.value as ValueType)} />
                  )}
                </Option>
              ))}
            </OptGroup>
          );
        }

        const selected = option.value === selectValue;
        const selectLabel = labelFormatter ? labelFormatter(option.label, selected) : option.label;
        return (
          <AntdSelect.Option
            key={option.value}
            {...option}
            value={option.value}
            label={selectLabel}
          >
            {selectLabel}
            {onDelete && (
              <DeleteButton onClick={() => onDelete(option.value as ValueType)} />
            )}
          </AntdSelect.Option>
        );
      })}
    </AntdSelect>
  );
}
